import { supabase } from './supabase';

export const standingsService = {
  async getAllSeasons() {
    try {
      const { data, error } = await supabase
        .from('seasons')
        .select('*')
        .order('season_number', { ascending: false });

      if (error) {
        console.error('Error fetching seasons:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Error in getAllSeasons:', err);
      return [];
    }
  },

  async getStandingsBySeason(seasonId) {
    try {
      const { data, error } = await supabase
        .from('team_standings')
        .select(`
          *,
          teams (
            id,
            name,
            abbreviation,
            logo_url,
            conferences (
              id,
              name,
              abbreviation
            )
          )
        `)
        .eq('season_id', seasonId)
        .order('rank', { ascending: true });

      if (error) {
        console.error('Error fetching standings:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Error in getStandingsBySeason:', err);
      return [];
    }
  },

  async getStandingsByConference(seasonId, conferenceId) {
    try {
      const { data, error } = await supabase
        .from('team_standings')
        .select(`
          *,
          teams (
            id,
            name,
            abbreviation,
            logo_url,
            conferences (
              id,
              name,
              abbreviation
            )
          )
        `)
        .eq('season_id', seasonId)
        .eq('teams.conference_id', conferenceId)
        .order('rank', { ascending: true });

      if (error) {
        console.error('Error fetching conference standings:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Error in getStandingsByConference:', err);
      return [];
    }
  },

  async getConferences() {
    try {
      const { data, error } = await supabase
        .from('conferences')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching conferences:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Error in getConferences:', err);
      return [];
    }
  },

  calculateRankings(teams) {
    const sorted = [...teams].sort((a, b) => {
      const aGamesPlayed = a.wins + a.losses;
      const bGamesPlayed = b.wins + b.losses;
      
      const aWinPct = aGamesPlayed === 0 ? 0 : a.wins / aGamesPlayed;
      const bWinPct = bGamesPlayed === 0 ? 0 : b.wins / bGamesPlayed;
      
      const aDiff = a.points_for - a.points_against;
      const bDiff = b.points_for - b.points_against;

      if (aGamesPlayed === 0 && bGamesPlayed === 0) {
        return 0;
      }
      
      if (aGamesPlayed === 0) return 1;
      if (bGamesPlayed === 0) return -1;

      if (bWinPct !== aWinPct) {
        return bWinPct - aWinPct;
      }

      if (bDiff !== aDiff) {
        return bDiff - aDiff;
      }

      return b.points_for - a.points_for;
    });

    return sorted.map((team, index) => ({
      ...team,
      rank: index + 1,
    }));
  },

  async updateRankingsBySeason(seasonId) {
    try {
      const { data, error: fetchError } = await supabase
        .from('team_standings')
        .select(`
          *,
          teams (
            id,
            name,
            abbreviation,
            logo_url,
            conferences (
              id,
              name,
              abbreviation
            )
          )
        `)
        .eq('season_id', seasonId);

      if (fetchError) {
        console.error('Error fetching standings:', fetchError);
        throw fetchError;
      }

      const rankedTeams = this.calculateRankings(data);

      const updates = rankedTeams.map(team => ({
        id: team.id,
        rank: team.rank
      }));

      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('team_standings')
          .update({ rank: update.rank })
          .eq('id', update.id);

        if (updateError) {
          console.error('Error updating ranking:', updateError);
          throw updateError;
        }
      }

      return rankedTeams;
    } catch (err) {
      console.error('Error in updateRankingsBySeason:', err);
      throw err;
    }
  },

  async updateRankingsByConference(seasonId, conferenceId) {
    try {
      const { data, error: fetchError } = await supabase
        .from('team_standings')
        .select(`
          *,
          teams (
            id,
            name,
            abbreviation,
            logo_url,
            conferences (
              id,
              name,
              abbreviation
            )
          )
        `)
        .eq('season_id', seasonId)
        .eq('teams.conference_id', conferenceId);

      if (fetchError) {
        console.error('Error fetching standings:', fetchError);
        throw fetchError;
      }

      const rankedTeams = this.calculateRankings(data);

      const updates = rankedTeams.map(team => ({
        id: team.id,
        rank: team.rank
      }));

      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('team_standings')
          .update({ rank: update.rank })
          .eq('id', update.id);

        if (updateError) {
          console.error('Error updating ranking:', updateError);
          throw updateError;
        }
      }

      return rankedTeams;
    } catch (err) {
      console.error('Error in updateRankingsByConference:', err);
      throw err;
    }
  }
};
